import { useFlatMessages } from '@/lib/hooks/useMessages';
import { useStreamingMessage } from '@/lib/hooks/useStreamingMessage';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Loader2, MessageSquare, Globe, Brain } from 'lucide-react';
import ChatMessage from './ChatMessage';
import StreamingMessage from './StreamingMessage';

interface ChatAreaProps {
  conversationId: string | null;
}

function ChatArea({ conversationId }: ChatAreaProps) {
  const {
    messages,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFlatMessages(conversationId);

  const {
    streamingContent,
    isStreaming,
    isConnecting,
    isCompleted,
    isSearchingWeb,
    isThinking,
    reasoningSummary,
    annotations,
    error: streamingError,
  } = useStreamingMessage();

  // 1. FIRST: Check if streaming is active (highest priority)
  //    Show streaming content regardless of conversationId or loading state
  if (isStreaming || isConnecting || streamingContent) {
    return (
      <div className="h-full overflow-y-auto flex flex-col-reverse bg-card">
        <div className="max-w-3xl mx-auto w-full p-4 space-y-6">
          {/* Show existing messages if available */}
          {messages.length > 0 && (
            <>
              {hasNextPage && (
                <div className="text-center py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Caricamento...
                      </>
                    ) : (
                      'Carica messaggi precedenti'
                    )}
                  </Button>
                </div>
              )}
              {messages.map((message, index) => (
                <ChatMessage key={message.id || `${message.createdAt}-${index}`} message={message} />
              ))}
            </>
          )}

          {/* Connecting / web search / thinking indicator */}
          {(isConnecting || (isSearchingWeb && !streamingContent) || (isThinking && !streamingContent)) && (
            <div className="flex gap-3">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 text-gray-500">
                  {isSearchingWeb ? (
                    <>
                      <Globe className="h-4 w-4 animate-pulse" />
                      <span className="text-sm">Cerco sul web...</span>
                    </>
                  ) : isThinking ? (
                    <>
                      <Brain className="h-4 w-4 animate-pulse" />
                      <span className="text-sm">Sto ragionando...</span>
                    </>
                  ) : (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Sto pensando...</span>
                    </>
                  )}
                </div>
                {/* Show reasoning summary in real-time during thinking */}
                {isThinking && reasoningSummary && (
                  <div className="text-xs text-muted-foreground italic pl-6 whitespace-pre-wrap">
                    {reasoningSummary}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Thinking indicator during streaming (reasoning still active while text streams) */}
          {isThinking && streamingContent && (
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <Brain className="h-3.5 w-3.5 animate-pulse" />
              <span className="text-xs">Sto ragionando...</span>
            </div>
          )}

          {/* Web search indicator during streaming */}
          {isSearchingWeb && streamingContent && (
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <Globe className="h-3.5 w-3.5 animate-pulse" />
              <span className="text-xs">Cerco sul web...</span>
            </div>
          )}

          {/* Streaming content */}
          {streamingContent && <StreamingMessage content={streamingContent} annotations={annotations} reasoningSummary={reasoningSummary} />}

          {/* Streaming error */}
          {streamingError && (
            <Alert variant="danger" className="max-w-md">
              <AlertDescription>
                Errore durante lo streaming: {streamingError.message}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    );
  }

  // 2. No conversation selected - show empty state
  if (!conversationId) {
    return (
      <div className="h-full flex items-center justify-center p-8 bg-card">
        <div className="text-center space-y-4 max-w-md text-gray-500">
          <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium">Nessuna conversazione selezionata</p>
          <p className="text-sm text-muted-foreground">
            Inizia una nuova chat o seleziona una conversazione esistente
          </p>
        </div>
      </div>
    );
  }

  // 3. Loading messages
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center p-4 bg-card">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="h-full flex items-center justify-center p-8 bg-card">
        <Alert variant="danger" className="max-w-md">
          <AlertDescription>
            Errore nel caricamento della conversazione: {error?.message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto flex flex-col-reverse bg-card">
      <div className="max-w-3xl mx-auto w-full p-4 space-y-6">
        {/* Load more button at top of conversation */}
        {hasNextPage && (
          <div className="text-center py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Caricamento...
                </>
              ) : (
                'Carica messaggi precedenti'
              )}
            </Button>
          </div>
        )}

        {/* Empty state */}
        {messages.length === 0 && !isStreaming ? (
          <div className="flex flex-col items-center justify-center text-gray-500 py-12">
            <MessageSquare className="h-12 w-12 mb-4 text-gray-400" />
            <p className="text-lg font-medium">Nessun messaggio</p>
            <p className="text-sm text-muted-foreground">Inizia una conversazione</p>
          </div>
        ) : (
          <>
            {/* Messages */}
            {messages.map((message, index) => (
              <ChatMessage key={message.id || `${message.createdAt}-${index}`} message={message} />
            ))}

            {/* Show loading indicator when connecting, searching web, or thinking */}
            {(isConnecting || (isSearchingWeb && !streamingContent) || (isThinking && !streamingContent)) && (
              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 text-gray-500">
                    {isSearchingWeb ? (
                      <>
                        <Globe className="h-4 w-4 animate-pulse" />
                        <span className="text-sm">Cerco sul web...</span>
                      </>
                    ) : isThinking ? (
                      <>
                        <Brain className="h-4 w-4 animate-pulse" />
                        <span className="text-sm">Sto ragionando...</span>
                      </>
                    ) : (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Sto pensando...</span>
                      </>
                    )}
                  </div>
                  {/* Show reasoning summary in real-time during thinking */}
                  {isThinking && reasoningSummary && (
                    <div className="text-xs text-muted-foreground italic pl-6 whitespace-pre-wrap">
                      {reasoningSummary}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Thinking indicator during streaming */}
            {isThinking && streamingContent && (
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Brain className="h-3.5 w-3.5 animate-pulse" />
                <span className="text-xs">Sto ragionando...</span>
              </div>
            )}

            {/* Web search indicator during streaming */}
            {isSearchingWeb && streamingContent && (
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Globe className="h-3.5 w-3.5 animate-pulse" />
                <span className="text-xs">Cerco sul web...</span>
              </div>
            )}

            {/* Show streaming message when content arrives or just completed */}
            {(isStreaming || isCompleted) && streamingContent && <StreamingMessage content={streamingContent} annotations={annotations} reasoningSummary={reasoningSummary} />}

            {/* Streaming error */}
            {streamingError && (
              <Alert variant="danger" className="max-w-md">
                <AlertDescription>
                  Errore durante lo streaming: {streamingError.message}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ChatArea;
